/**
 * DEFCAT-R2-16 / R2-12 — client-side `effective(L)` allocation resolve.
 *
 * Implements the EXACT algorithm from
 * `_BACKEND/contracts/taxonomy/v1/design-notes/allocation-inheritance.md`:
 *
 *   effective(L) ∋ T  ⇔  ∃ include row (La, Ta):
 *                            La ∈ {L} ∪ ancestors(L)     -- location walk-up
 *                            Ta ∈ {T} ∪ ancestors(T)     -- type-group expansion
 *                     AND NOT ∃ exclusion row (L, Te):
 *                            Te ∈ {T} ∪ ancestors(T)     -- node-LOCAL exclusion
 *
 * Key properties (design note §"Rules that follow"):
 *   1. Children inherit a group's includes (location walk-up).
 *   4. Type-group rows expand to leaves on BOTH the include and exclusion side.
 *   3. Exclusions are NODE-LOCAL to L — an exclusion on an ancestor of L does
 *      NOT subtract from effective(L) (only an exclusion whose location IS L).
 *   6. Empty overlay (zero pairs) ⇒ no gating, every type valid → returns null.
 *
 * Returns the set of valid TYPE entry ids (groups + leaves; callers filter to
 * leaves for the picker), or `null` when the type list should NOT be gated
 * (independent catalogue, or no location chosen yet) — matching the contract
 * of the older `validTypeEntryIds` it supersedes.
 *
 * Pure (no React) so the inheritance + exclusion logic is unit-tested against
 * the design note's worked example in isolation.
 */
import type { CatalogueValidityPair } from "@aeriosiq/types";

interface NodeLike {
  entryId: string;
  parentEntryId?: string | null;
  /**
   * DEFCAT-R2-13 — location entries hang under an L0 component group (the
   * root of each location sub-tree carries it; children inherit it). The
   * walk-up chain must END at this L0, because allocations may be written AT
   * L0 level and those wire pairs carry the RAW component-group uuid as
   * `locationEntryId` (see `treeBuild.nodeIdToRawL0` in apps/web).
   */
  componentGroupId?: string | null;
}

/** Walk parent links to collect {id} ∪ ancestors(id). Cycle-safe. */
function ancestorChain(
  id: string,
  parentOf: ReadonlyMap<string, string | null>,
): Set<string> {
  const chain = new Set<string>();
  let cur: string | null | undefined = id;
  while (cur && !chain.has(cur)) {
    chain.add(cur);
    cur = parentOf.get(cur) ?? null;
  }
  return chain;
}

function parentMap(nodes: readonly NodeLike[]): Map<string, string | null> {
  const m = new Map<string, string | null>();
  for (const n of nodes) m.set(n.entryId, n.parentEntryId ?? null);
  return m;
}

const isExclusion = (p: CatalogueValidityPair): boolean => p.exclusion === true;

/**
 * Resolve `effective(L)` per the design note. `null` ⇒ ungated (no overlay or
 * no location chosen). Otherwise a set of every TYPE entry id valid at the
 * chosen location.
 */
export function effectiveTypeEntryIds(
  pairs: readonly CatalogueValidityPair[] | null | undefined,
  locationEntries: readonly NodeLike[],
  typeEntries: readonly NodeLike[],
  chosenLocationEntryId: string | null,
): Set<string> | null {
  // Rule 6 — empty overlay keeps the long-standing "independent" meaning.
  if (!pairs || pairs.length === 0) return null;
  if (!chosenLocationEntryId) return null; // not narrowed yet

  const locParent = parentMap(locationEntries);
  const typeParent = parentMap(typeEntries);
  const locChain = ancestorChain(chosenLocationEntryId, locParent);

  // DEFCAT-R2-13 read-side (drawer bug, Christian 2026-06-04: "I cannot
  // select a defect — the widget is disabled"): allocations written at L0
  // (component-group) level arrive as pairs whose `locationEntryId` is the
  // RAW component-group uuid — never a catalogue entryId, so the parent
  // walk-up above can't reach them and effective(L) resolved to ∅ for every
  // location of a catalogue allocated at L0. Append the chosen location's
  // resolved L0 group to the chain (the same chain tail the editor's
  // `allocationResolve.locationChain` appends as its synthetic `l0:` node).
  const locById = new Map(locationEntries.map((e) => [e.entryId, e]));
  for (const id of locChain) {
    const cg = locById.get(id)?.componentGroupId;
    if (cg != null && cg.length > 0) {
      locChain.add(cg);
      break; // nearest carrier wins — one L0 per sub-tree
    }
  }

  // Include TYPE NODES whose pair's location is L or any ancestor of L
  // (location walk-up). These are the "rule targets" before type-group expand.
  const includeTypeNodes = new Set<string>();
  // Exclusion TYPE NODES whose pair's location IS L exactly (node-local; rule 3).
  const excludeTypeNodes = new Set<string>();
  for (const p of pairs) {
    if (isExclusion(p)) {
      if (p.locationEntryId === chosenLocationEntryId) {
        excludeTypeNodes.add(p.typeEntryId);
      }
    } else if (locChain.has(p.locationEntryId)) {
      includeTypeNodes.add(p.typeEntryId);
    }
  }

  if (includeTypeNodes.size === 0) return new Set<string>(); // nothing valid here

  // A type entry T is included iff any node on T's ancestor chain is an include
  // rule target (rule 4 — type-group rows expand to leaves), AND no node on T's
  // chain is a node-local exclusion target (rule 4 expansion on exclusions too).
  const valid = new Set<string>();
  for (const t of typeEntries) {
    const chain = ancestorChain(t.entryId, typeParent);
    let included = false;
    let excluded = false;
    for (const anc of chain) {
      if (includeTypeNodes.has(anc)) included = true;
      if (excludeTypeNodes.has(anc)) excluded = true;
    }
    if (included && !excluded) valid.add(t.entryId);
  }
  return valid;
}
